import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { PageHeader } from "@/components/common";
import HrisUsers from "./HrisUsers";
import HrisRoles from "./HrisRoles";
import HrisApprovals from "./HrisApprovals";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "users", label: "User Management", icon: Icons.UserCog, desc: "Everyone in the Employee Directory. Set each person's HRIS role, status, and which companies, locations and projects they can see.", accent: "bg-blue-50 text-blue-700", el: HrisUsers },
  { key: "roles", label: "Roles & Permissions", icon: Icons.ShieldCheck, desc: "Create HRIS roles, tick the features each role can open, then save the permissions.", accent: "bg-violet-50 text-violet-700", el: HrisRoles },
  { key: "approvals", label: "Approval Workflow", icon: Icons.CheckCircle2, desc: "Choose which users or roles review changes to payroll, config and masterfile records.", accent: "bg-emerald-50 text-emerald-700", el: HrisApprovals },
];

const Overview = ({ go }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {TABS.map(({ key, label, desc, icon: Icon, accent }) => (
      <button key={key} onClick={() => go(key)} className="text-left group" data-testid={`hris-settings-card-${key}`}>
        <div className="h-full rounded-xl border border-slate-200 bg-white p-6 space-y-3 transition-shadow hover:shadow-lg hover:border-blue-300">
          <div className={`h-12 w-12 rounded-xl grid place-items-center ${accent}`}><Icon className="h-6 w-6" /></div>
          <div><h3 className="font-semibold text-lg text-slate-900">{label}</h3><p className="text-sm text-slate-500 mt-1">{desc}</p></div>
          <div className="inline-flex items-center text-sm font-medium text-blue-700">Open <Icons.ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" /></div>
        </div>
      </button>
    ))}
    <div className="md:col-span-2 lg:col-span-3 rounded-xl border border-slate-200 bg-slate-50/60 p-5" data-testid="hris-settings-flow">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">How it works</div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
        {["Create a role", "Tick its permissions", "Save permissions", "Assign the role to users", "Use the role in Approval Workflow"].map((s, i, a) => (
          <React.Fragment key={s}>
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5"><span className="h-5 w-5 rounded-full bg-blue-600 text-white text-[11px] grid place-items-center font-semibold">{i + 1}</span>{s}</span>
            {i < a.length - 1 && <Icons.ChevronRight className="h-4 w-4 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">HRIS roles add access on top of the roles in Master Settings. People without a created role keep the default Employee access (Clock In/Out, Forms and their own records).</p>
    </div>
  </div>
);

export default function HrisSettings() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const go = (k) => navigate(k ? `/hris/settings/${k}` : "/hris/settings");
  const active = TABS.find((t) => t.key === tab);
  const tabCls = (on) => `inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${on ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`;
  const El = active?.el;
  const { user } = useAuth();
  if (user?.hris_limited && !(user?.hris_perms || []).includes("set.all")) {
    return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="hris-settings-denied">HRIS Settings are only available to the module operator.</div>;
  }
  return (
    <div className="space-y-4" data-testid="hris-settings-page">
      <PageHeader title="HRIS Settings" subtitle="HRIS administration: users, roles & permissions, and approval workflow." />
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        <button className={tabCls(!active)} onClick={() => go("")} data-testid="hris-settings-tab-overview"><Icons.Shield className="h-4 w-4" /> Overview</button>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} className={tabCls(active?.key === key)} onClick={() => go(key)} data-testid={`hris-settings-tab-${key}`}><Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>
      {El ? <El /> : <Overview go={go} />}
    </div>
  );
}
